import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import AssetDetailSection from '../../assets/components/AssetDetailSection';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import RowActionsMenu from '../../../components/RowActionsMenu';
import { makeAssetDetailsUrl } from '../../../routes';
import {
    invalidateInventoryReaders,
    useRetireObjectMutation,
} from '../../../api/inventory/inventoryApiSlice';
import { readInventoryFailure } from '../../../api/inventory/inventoryErrors';
import { makeHasPermissionSelector } from '../../../selector/authenticationSelector';
import Permission from '../../../common/permissions';
import {
    setConfirmationDialogContent,
    setIsConfirmationDialog,
} from '../../../store/slices/globalSlice';
import {
    FLEET_STATE_BADGE,
    ProductId,
    fleetStateLabelKey,
    formatFleetAddress,
    useProductFleet,
} from '../productFleet';
import { onRowClick } from '../../../utils/rowClick';

interface AssignedObjectsSectionProps {
    productId: ProductId;
}

/**
 * The fleet fulfilling one product — which physical objects it has, where each
 * one is, and whether it is free right now.
 *
 * The Station and its address live on these rows rather than at product level:
 * a product is location-independent by design and may be offered at several
 * (architecture §4), so "where is it" is only answerable per object.
 *
 * State is whatever the backend reconciled — this component never recomputes
 * it. The rule ("is anyone still holding this object") is enforced behind the
 * API (ADR-0020) by `liveHolderRentalItemWhere`, and a client-side copy would
 * be a second definition of a question that has cost us production incidents.
 */
const AssignedObjectsSection: React.FC<AssignedObjectsSectionProps> = ({ productId }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { objects, isLoading, isError, error } = useProductFleet(productId);

    const hasDeletePermission = useSelector(
        makeHasPermissionSelector(Permission.deleteAssets()),
    );
    const [retireObject, { isLoading: isRetiring }] = useRetireObjectMutation();
    const [pendingRetireId, setPendingRetireId] = useState<string | null>(null);
    const [retireError, setRetireError] = useState<string | null>(null);

    const positionLabel = t('product_detail.objects.position');

    const handleClickRetire = useCallback(
        (assetId: string, objectName: string) => {
            setRetireError(null);
            setPendingRetireId(assetId);
            dispatch(setIsConfirmationDialog(true));
            dispatch(
                setConfirmationDialogContent({
                    content: t('product_detail.objects.confirm_retire.content', {
                        name: objectName,
                    }),
                    headerText: t('product_detail.objects.confirm_retire.header'),
                    leftButtonText: t('common.cancel'),
                    rightButtonText: t('product_detail.objects.confirm_retire.confirm'),
                }),
            );
        },
        [dispatch, t],
    );

    const handleConfirmRetire = useCallback(async () => {
        if (!pendingRetireId) return;
        try {
            // The target-model retire (#2134): archives the object and frees its
            // position, keeping `doormapping` / `rentaldoormapping` so a past
            // booking can still be traced to the unit that served it. The old
            // `deleteAsset` hard-deleted those, destroying that trail.
            const result = await retireObject({ assetId: pendingRetireId }).unwrap();
            // Cross-slice: `invalidatesTags` cannot reach the fleet's own slice.
            invalidateInventoryReaders(dispatch);
            dispatch(setIsConfirmationDialog(false));
            setPendingRetireId(null);
            // A second retire is a 200, not an error — say so rather than
            // letting the row silently not change.
            if (result?.retired === false) {
                setRetireError(t('product_detail.objects.already_retired'));
            }
        } catch (err: unknown) {
            dispatch(setIsConfirmationDialog(false));
            setPendingRetireId(null);
            const failure = readInventoryFailure(err);
            // 409 blockers are the interesting case: the request was fine, the
            // object is held. Name the reason instead of a generic failure.
            if (failure.blockers.length > 0) {
                setRetireError(
                    failure.blockers
                        .map((blocker) => t(`product_detail.objects.blocker.${blocker}`, {
                            defaultValue: blocker,
                        }))
                        .join(' · '),
                );
                return;
            }
            setRetireError(failure.message ?? t('product_detail.objects.retire_error'));
        }
    }, [dispatch, pendingRetireId, retireObject, t]);

    return (
        <AssetDetailSection
            title={t('product_detail.sections.assigned_objects')}
            subtitle={t('product_detail.sections.assigned_objects_subtitle')}
        >
            {isLoading && (
                <p className={'text-sm text-foreground/60'}>
                    {t('common.loading')}
                </p>
            )}

            {/* An error is its own state, never an empty list: a fleet that
                failed to load must not read as a product with no objects.
                And the causes are not one state either — a 404 during a
                deploy window resolves itself, an unreachable service does
                not, so the reader is told which they are looking at rather
                than left to open devtools. */}
            {!isLoading && isError && error && (
                <p className={'text-sm text-foreground'}>
                    {t(`product_detail.objects.load_error.${error.kind}`)}
                    {error.status != null && ` (${error.status})`}
                </p>
            )}

            {!isLoading && !isError && objects.length === 0 && (
                <p className={'text-sm text-foreground/40 italic'}>
                    {t('product_detail.objects.empty')}
                </p>
            )}

            {!isLoading && !isError && objects.length > 0 && (
                <div className={'overflow-x-auto'}>
                    <table className={'w-full text-sm'}>
                        <thead>
                            <tr className={'border-b border-foreground/10'}>
                                <th className={'text-left py-2 text-foreground/60 font-medium'}>
                                    {t('product_detail.objects.columns.object')}
                                </th>
                                <th className={'text-left py-2 text-foreground/60 font-medium'}>
                                    {t('product_detail.objects.columns.station')}
                                </th>
                                <th className={'text-left py-2 text-foreground/60 font-medium'}>
                                    {t('product_detail.objects.columns.state')}
                                </th>
                                <th className={'text-left py-2 text-foreground/60 font-medium'}>
                                    {t('product_detail.objects.columns.booking')}
                                </th>
                                {hasDeletePermission && (
                                    <th className={'text-right py-2 text-foreground/60 font-medium'}>
                                        {t('product_detail.objects.columns.actions')}
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {objects.map((object) => {
                                const address = formatFleetAddress(object);
                                return (
                                    <tr
                                        key={object.assetId}
                                        // A click on the row opens the object (design-decisions.md
                                        // › Zeilenaktionen in Tabellen); the name stays a link,
                                        // which is the keyboard's way in.
                                        onClick={onRowClick(() => navigate(makeAssetDetailsUrl(object.assetId)))}
                                        className={'border-b border-foreground/5 align-top cursor-pointer hover:bg-foreground/5 transition-colors'}
                                    >
                                        <td className={'py-2 pr-4'}>
                                            <Link
                                                to={makeAssetDetailsUrl(object.assetId)}
                                                className={'text-primary hover:underline'}
                                                onClick={(event) => event.stopPropagation()}
                                            >
                                                {object.name}
                                            </Link>
                                            {object.doors.length > 0 && (
                                                <div className={'text-xs text-foreground/60'}>
                                                    {positionLabel} {object.doors.join(', ')}
                                                </div>
                                            )}
                                        </td>
                                        <td className={'py-2 pr-4'}>
                                            {/* A fictional depot is a placeholder the room
                                                path creates, not a cabinet anyone can walk
                                                to — naming it would be misleading. */}
                                            {object.station && !object.station.isFictional
                                                ? object.station.name
                                                : t('common.not_applicable')}
                                            <div className={'text-xs text-foreground/60'}>
                                                {object.location?.name ?? t('common.not_applicable')}
                                                {address && <> · {address}</>}
                                            </div>
                                        </td>
                                        <td className={'py-2 pr-4'}>
                                            <span
                                                className={`px-2 py-0.5 rounded-full text-xs font-medium ${FLEET_STATE_BADGE[object.state]}`}
                                            >
                                                {t(fleetStateLabelKey(object.state))}
                                            </span>
                                        </td>
                                        <td className={'py-2'}>
                                            {object.holder?.bookingNumber ? (
                                                <span>{object.holder.bookingNumber}</span>
                                            ) : (
                                                <span className={'text-foreground/40'}>
                                                    {t('common.not_applicable')}
                                                </span>
                                            )}
                                        </td>
                                        {hasDeletePermission && (
                                            <td className={'py-2 text-right'}>
                                                {/* An occupied object cannot be retired —
                                                    someone is holding it right now, and
                                                    removing it would strand their booking.
                                                    The API is the enforcer; this only
                                                    explains the rule earlier (ADR-0020).
                                                    Retiring changes something, so it sits
                                                    in the row's menu. */}
                                                <RowActionsMenu
                                                    rowName={object.name}
                                                    items={[{
                                                        id: 'retire',
                                                        label: t('product_detail.objects.retire'),
                                                        onClick: () => handleClickRetire(object.assetId, object.name),
                                                        disabled: object.state === 'inUse' || isRetiring,
                                                        hint: object.state === 'inUse'
                                                            ? t('product_detail.objects.retire_blocked_in_use')
                                                            : undefined,
                                                    }]}
                                                />
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {retireError && (
                <p className={'mt-3 text-sm text-foreground'}>
                    {t('product_detail.objects.retire_error')}: {retireError}
                </p>
            )}

            <ConfirmationDialog
                handleClickConfirm={handleConfirmRetire}
                isLoading={isRetiring}
            />
        </AssetDetailSection>
    );
};

export default AssignedObjectsSection;
